import type { CaptureFrame, CaptureSession, ExportExtras } from "./types";

const FBX_TIME_SECOND = 46186158000;
const APP_NAME = "Tony Marghella Face Capture";
const MARKER_SIZE = 1.45;

type Vec3 = [number, number, number];
type FbxType =
  | "Geometry"
  | "Model"
  | "Material"
  | "AnimationStack"
  | "AnimationLayer"
  | "AnimationCurveNode"
  | "AnimationCurve";

type BuildOptions = {
  includeAnimation?: boolean;
  extras?: ExportExtras;
};

type BuildState = {
  objects: string[];
  connections: string[];
  counts: Record<FbxType, number>;
};

type MeshSpec = {
  name: string;
  vertices: Vec3[];
  polygons: number[][];
  color: Vec3;
};

function createBuildState(): BuildState {
  return {
    objects: [],
    connections: [],
    counts: {
      Geometry: 0,
      Model: 0,
      Material: 0,
      AnimationStack: 0,
      AnimationLayer: 0,
      AnimationCurveNode: 0,
      AnimationCurve: 0,
    },
  };
}

function addObject(state: BuildState, type: FbxType, block: string) {
  state.objects.push(block);
  state.counts[type] += 1;
}

function cleanName(value: string): string {
  return value.replace(/[^A-Za-z0-9_]/g, "_").replace(/^([0-9])/, "_$1");
}

function landmarkToWorld(landmark: { x: number; y: number; z: number }): Vec3 {
  return [
    (landmark.x - 0.5) * 180,
    (0.5 - landmark.y) * 130,
    -landmark.z * 220,
  ];
}

function poseLandmarkToWorld(landmark: { x: number; y: number; z: number }): Vec3 {
  return [
    (landmark.x - 0.5) * 220,
    (0.5 - landmark.y) * 220,
    -landmark.z * 220,
  ];
}

function matrixTranslation(frame: CaptureFrame): Vec3 {
  const data = frame.matrix;
  if (data.length >= 16) {
    return [data[12] * 10, data[13] * 10, data[14] * 10];
  }
  const nose = frame.landmarks[1] ?? frame.landmarks[0];
  return landmarkToWorld(nose);
}

function fmt(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return Number(value.toFixed(6)).toString();
}

function arr(values: Array<string | number>): string {
  return values.join(",");
}

function idFactory(start = 100000): () => number {
  let current = start;
  return () => current++;
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function normalize(value: Vec3): Vec3 {
  const length = Math.hypot(value[0], value[1], value[2]);
  if (length < 0.000001) return [0, 0, 1];
  return [value[0] / length, value[1] / length, value[2] / length];
}

function validTriangles(triangles: number[], vertexCount: number): number[][] {
  const polygons: number[][] = [];
  for (let index = 0; index + 2 < triangles.length; index += 3) {
    const tri = [triangles[index], triangles[index + 1], triangles[index + 2]];
    if (tri.every((item) => Number.isInteger(item) && item >= 0 && item < vertexCount)) {
      polygons.push(tri);
    }
  }
  return polygons;
}

function fallbackPolygons(vertexCount: number): number[][] {
  const polygons: number[][] = [];
  for (let index = 0; index + 2 < vertexCount; index += 3) {
    polygons.push([index, index + 1, index + 2]);
  }
  return polygons;
}

function markerGeometry(landmarks: CaptureFrame["landmarks"]): { vertices: Vec3[]; polygons: number[][] } {
  const vertices: Vec3[] = [];
  const polygons: number[][] = [];

  for (const landmark of landmarks) {
    const center = landmarkToWorld(landmark);
    const base = vertices.length;
    vertices.push(
      [center[0] + MARKER_SIZE, center[1], center[2]],
      [center[0] - MARKER_SIZE, center[1], center[2]],
      [center[0], center[1] + MARKER_SIZE, center[2]],
      [center[0], center[1], center[2] + MARKER_SIZE],
    );
    polygons.push([base, base + 2, base + 3], [base + 2, base + 1, base + 3], [base + 1, base, base + 3], [base, base + 1, base + 2]);
  }

  return { vertices, polygons };
}

function bounds(points: Vec3[]) {
  const min: Vec3 = [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];
  const max: Vec3 = [Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY];
  for (const point of points) {
    min[0] = Math.min(min[0], point[0]);
    min[1] = Math.min(min[1], point[1]);
    min[2] = Math.min(min[2], point[2]);
    max[0] = Math.max(max[0], point[0]);
    max[1] = Math.max(max[1], point[1]);
    max[2] = Math.max(max[2], point[2]);
  }
  return {
    min,
    max,
    center: [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2] as Vec3,
    size: [max[0] - min[0], max[1] - min[1], max[2] - min[2]] as Vec3,
  };
}

function pointAt(frame: CaptureFrame, index: number, fallback: Vec3): Vec3 {
  const landmark = frame.landmarks[index];
  return landmark ? landmarkToWorld(landmark) : fallback;
}

function posePointAt(frame: CaptureFrame, index: number): Vec3 | null {
  const landmark = frame.poseLandmarks?.[index];
  if (!landmark || (landmark.visibility ?? 1) < 0.28) return null;
  return poseLandmarkToWorld(landmark);
}

function averagePoints(points: Vec3[]): Vec3 {
  if (!points.length) return [0, 0, 0];
  return [
    points.reduce((sum, point) => sum + point[0], 0) / points.length,
    points.reduce((sum, point) => sum + point[1], 0) / points.length,
    points.reduce((sum, point) => sum + point[2], 0) / points.length,
  ];
}

function poseAnimationValues(frames: CaptureFrame[], index: number, fallback: Vec3): Vec3[] {
  let last = fallback;
  return frames.map((frame) => {
    const landmark = frame.poseLandmarks?.[index];
    if (landmark && (landmark.visibility ?? 1) >= 0.28) {
      last = poseLandmarkToWorld(landmark);
    }
    return last;
  });
}

function ellipsoidMesh(name: string, center: Vec3, radii: Vec3, color: Vec3, segments = 18, rings = 10): MeshSpec {
  const vertices: Vec3[] = [];
  const polygons: number[][] = [];

  for (let ring = 0; ring <= rings; ring += 1) {
    const theta = (ring / rings) * Math.PI;
    const sinTheta = Math.sin(theta);
    const cosTheta = Math.cos(theta);
    for (let segment = 0; segment < segments; segment += 1) {
      const phi = (segment / segments) * Math.PI * 2;
      vertices.push([
        center[0] + Math.cos(phi) * sinTheta * radii[0],
        center[1] + cosTheta * radii[1],
        center[2] + Math.sin(phi) * sinTheta * radii[2],
      ]);
    }
  }

  for (let ring = 0; ring < rings; ring += 1) {
    for (let segment = 0; segment < segments; segment += 1) {
      const nextSegment = (segment + 1) % segments;
      const a = ring * segments + segment;
      const b = ring * segments + nextSegment;
      const c = (ring + 1) * segments + nextSegment;
      const d = (ring + 1) * segments + segment;
      if (ring !== 0) polygons.push([a, b, d]);
      if (ring !== rings - 1) polygons.push([b, c, d]);
    }
  }

  return { name, vertices, polygons, color };
}

function cylinderMesh(name: string, start: Vec3, end: Vec3, radius: number, color: Vec3, segments = 14): MeshSpec {
  const axis = normalize(sub(end, start));
  const helper: Vec3 = Math.abs(axis[1]) > 0.82 ? [1, 0, 0] : [0, 1, 0];
  const sideA = normalize(cross(axis, helper));
  const sideB = normalize(cross(axis, sideA));
  const vertices: Vec3[] = [];
  const polygons: number[][] = [];

  for (const center of [start, end]) {
    for (let segment = 0; segment < segments; segment += 1) {
      const angle = (segment / segments) * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      vertices.push([
        center[0] + sideA[0] * x + sideB[0] * y,
        center[1] + sideA[1] * x + sideB[1] * y,
        center[2] + sideA[2] * x + sideB[2] * y,
      ]);
    }
  }

  const startCenter = vertices.length;
  vertices.push(start);
  const endCenter = vertices.length;
  vertices.push(end);

  for (let segment = 0; segment < segments; segment += 1) {
    const nextSegment = (segment + 1) % segments;
    const a = segment;
    const b = nextSegment;
    const c = segments + nextSegment;
    const d = segments + segment;
    polygons.push([a, b, d], [b, c, d], [startCenter, a, b], [endCenter, c, d]);
  }

  return { name, vertices, polygons, color };
}

function proxyGeometry(frame: CaptureFrame, extras: ExportExtras | undefined): MeshSpec[] {
  if (!extras) return [];

  const facePoints = frame.landmarks.map(landmarkToWorld);
  const faceBounds = bounds(facePoints);
  const faceWidth = Math.max(faceBounds.size[0], 32);
  const faceHeight = Math.max(faceBounds.size[1], 42);
  const faceDepth = Math.max(faceBounds.size[2], 16);
  const center = faceBounds.center;
  const chinY = faceBounds.min[1];
  const foreheadY = faceBounds.max[1];
  const meshes: MeshSpec[] = [];

  if (extras.head) {
    meshes.push(ellipsoidMesh(
      "Proxy_Head",
      [center[0], center[1] + faceHeight * 0.02, center[2] + faceDepth * 0.08],
      [faceWidth * 0.62, faceHeight * 0.72, Math.max(28, faceDepth * 1.65)],
      [0.38, 0.62, 0.96],
      22,
      12,
    ));
  }

  if (extras.eyes) {
    const leftEye = pointAt(frame, 33, [center[0] - faceWidth * 0.18, foreheadY - faceHeight * 0.42, center[2] + faceDepth * 0.2]);
    const rightEye = pointAt(frame, 263, [center[0] + faceWidth * 0.18, foreheadY - faceHeight * 0.42, center[2] + faceDepth * 0.2]);
    meshes.push(
      ellipsoidMesh("Proxy_LeftEye", [leftEye[0], leftEye[1], leftEye[2] + 1.6], [faceWidth * 0.055, faceHeight * 0.035, faceDepth * 0.18], [0.92, 0.96, 1], 14, 8),
      ellipsoidMesh("Proxy_RightEye", [rightEye[0], rightEye[1], rightEye[2] + 1.6], [faceWidth * 0.055, faceHeight * 0.035, faceDepth * 0.18], [0.92, 0.96, 1], 14, 8),
    );
  }

  const mouthCenter = averagePoints([
    pointAt(frame, 13, [center[0], chinY + faceHeight * 0.3, center[2]]),
    pointAt(frame, 14, [center[0], chinY + faceHeight * 0.22, center[2]]),
    pointAt(frame, 61, [center[0] - faceWidth * 0.18, chinY + faceHeight * 0.27, center[2]]),
    pointAt(frame, 291, [center[0] + faceWidth * 0.18, chinY + faceHeight * 0.27, center[2]]),
  ]);

  if (extras.teeth) {
    meshes.push(
      ellipsoidMesh("Proxy_UpperTeeth", [mouthCenter[0], mouthCenter[1] + faceHeight * 0.035, mouthCenter[2] + faceDepth * 0.38], [faceWidth * 0.19, faceHeight * 0.035, faceDepth * 0.08], [0.97, 0.94, 0.84], 16, 6),
      ellipsoidMesh("Proxy_LowerTeeth", [mouthCenter[0], mouthCenter[1] - faceHeight * 0.035, mouthCenter[2] + faceDepth * 0.38], [faceWidth * 0.18, faceHeight * 0.03, faceDepth * 0.08], [0.97, 0.94, 0.84], 16, 6),
    );
  }

  if (extras.tongue) {
    meshes.push(ellipsoidMesh("Proxy_Tongue", [mouthCenter[0], mouthCenter[1] - faceHeight * 0.06, mouthCenter[2] + faceDepth * 0.48], [faceWidth * 0.14, faceHeight * 0.035, faceDepth * 0.2], [0.92, 0.28, 0.36], 16, 7));
  }

  if (frame.poseLandmarks?.length && (extras.neck || extras.torso || extras.arms || extras.fullBody)) {
    const leftShoulder = posePointAt(frame, 11);
    const rightShoulder = posePointAt(frame, 12);
    const leftElbow = posePointAt(frame, 13);
    const rightElbow = posePointAt(frame, 14);
    const leftWrist = posePointAt(frame, 15);
    const rightWrist = posePointAt(frame, 16);
    const leftHip = posePointAt(frame, 23);
    const rightHip = posePointAt(frame, 24);
    const leftKnee = posePointAt(frame, 25);
    const rightKnee = posePointAt(frame, 26);
    const leftAnkle = posePointAt(frame, 27);
    const rightAnkle = posePointAt(frame, 28);
    const nose = posePointAt(frame, 0);
    const shoulderSpan = leftShoulder && rightShoulder
      ? Math.hypot(leftShoulder[0] - rightShoulder[0], leftShoulder[1] - rightShoulder[1], leftShoulder[2] - rightShoulder[2])
      : faceWidth;
    const limbRadius = Math.max(2.4, shoulderSpan * 0.055);
    let usedPoseBody = false;

    if ((extras.neck || extras.fullBody) && leftShoulder && rightShoulder) {
      const shoulderCenter = averagePoints([leftShoulder, rightShoulder]);
      const neckTop = nose ? averagePoints([nose, shoulderCenter, shoulderCenter]) : [shoulderCenter[0], shoulderCenter[1] + faceHeight * 0.34, shoulderCenter[2]] as Vec3;
      meshes.push(cylinderMesh("Proxy_Neck", shoulderCenter, neckTop, Math.max(2.6, shoulderSpan * 0.075), [0.45, 0.72, 0.68], 16));
      usedPoseBody = true;
    }

    if ((extras.torso || extras.fullBody) && leftShoulder && rightShoulder && leftHip && rightHip) {
      const torsoCenter = averagePoints([leftShoulder, rightShoulder, leftHip, rightHip]);
      const torsoHeight = Math.max(24, Math.abs(averagePoints([leftShoulder, rightShoulder])[1] - averagePoints([leftHip, rightHip])[1]));
      meshes.push(ellipsoidMesh("Proxy_Torso", torsoCenter, [Math.max(18, shoulderSpan * 0.42), torsoHeight * 0.55, Math.max(12, shoulderSpan * 0.18)], [0.42, 0.58, 0.82], 18, 10));
      meshes.push(ellipsoidMesh("Proxy_Pelvis", averagePoints([leftHip, rightHip]), [Math.max(14, shoulderSpan * 0.32), Math.max(8, torsoHeight * 0.14), Math.max(10, shoulderSpan * 0.16)], [0.42, 0.58, 0.82], 16, 8));
      usedPoseBody = true;
    }

    if (extras.arms || extras.fullBody) {
      if (leftShoulder && leftElbow) {
        meshes.push(cylinderMesh("Proxy_LeftUpperArm", leftShoulder, leftElbow, limbRadius, [0.58, 0.5, 0.84], 14));
        usedPoseBody = true;
      }
      if (leftElbow && leftWrist) {
        meshes.push(cylinderMesh("Proxy_LeftForearm", leftElbow, leftWrist, limbRadius * 0.82, [0.58, 0.5, 0.84], 14));
        usedPoseBody = true;
      }
      if (rightShoulder && rightElbow) {
        meshes.push(cylinderMesh("Proxy_RightUpperArm", rightShoulder, rightElbow, limbRadius, [0.58, 0.5, 0.84], 14));
        usedPoseBody = true;
      }
      if (rightElbow && rightWrist) {
        meshes.push(cylinderMesh("Proxy_RightForearm", rightElbow, rightWrist, limbRadius * 0.82, [0.58, 0.5, 0.84], 14));
        usedPoseBody = true;
      }
    }

    if (extras.fullBody) {
      if (leftHip && leftKnee) {
        meshes.push(cylinderMesh("Proxy_LeftThigh", leftHip, leftKnee, limbRadius * 1.05, [0.5, 0.56, 0.78], 14));
        usedPoseBody = true;
      }
      if (leftKnee && leftAnkle) {
        meshes.push(cylinderMesh("Proxy_LeftShin", leftKnee, leftAnkle, limbRadius * 0.9, [0.5, 0.56, 0.78], 14));
        usedPoseBody = true;
      }
      if (rightHip && rightKnee) {
        meshes.push(cylinderMesh("Proxy_RightThigh", rightHip, rightKnee, limbRadius * 1.05, [0.5, 0.56, 0.78], 14));
        usedPoseBody = true;
      }
      if (rightKnee && rightAnkle) {
        meshes.push(cylinderMesh("Proxy_RightShin", rightKnee, rightAnkle, limbRadius * 0.9, [0.5, 0.56, 0.78], 14));
        usedPoseBody = true;
      }
    }

    if (usedPoseBody) {
      return meshes;
    }
  }

  const neckTop: Vec3 = [center[0], chinY - faceHeight * 0.08, center[2]];
  const neckBottom: Vec3 = [center[0], chinY - faceHeight * 0.48, center[2] - faceDepth * 0.08];
  if (extras.neck || extras.torso || extras.arms || extras.fullBody) {
    meshes.push(cylinderMesh("Proxy_Neck", neckTop, neckBottom, faceWidth * 0.15, [0.45, 0.72, 0.68], 16));
  }

  const shoulderY = neckBottom[1] - faceHeight * 0.15;
  const pelvisY = neckBottom[1] - faceHeight * 1.28;
  const shoulderLeft: Vec3 = [center[0] - faceWidth * 0.82, shoulderY, center[2] - faceDepth * 0.1];
  const shoulderRight: Vec3 = [center[0] + faceWidth * 0.82, shoulderY, center[2] - faceDepth * 0.1];

  if (extras.torso || extras.arms || extras.fullBody) {
    meshes.push(ellipsoidMesh("Proxy_Torso", [center[0], (shoulderY + pelvisY) / 2, center[2] - faceDepth * 0.22], [faceWidth * 0.62, Math.abs(shoulderY - pelvisY) * 0.56, faceDepth * 0.82], [0.42, 0.58, 0.82], 18, 10));
  }

  if (extras.arms || extras.fullBody) {
    const leftElbow: Vec3 = [center[0] - faceWidth * 1.08, shoulderY - faceHeight * 0.48, center[2] - faceDepth * 0.02];
    const rightElbow: Vec3 = [center[0] + faceWidth * 1.08, shoulderY - faceHeight * 0.48, center[2] - faceDepth * 0.02];
    const leftHand: Vec3 = [center[0] - faceWidth * 0.88, shoulderY - faceHeight * 0.98, center[2] + faceDepth * 0.02];
    const rightHand: Vec3 = [center[0] + faceWidth * 0.88, shoulderY - faceHeight * 0.98, center[2] + faceDepth * 0.02];
    meshes.push(
      cylinderMesh("Proxy_LeftUpperArm", shoulderLeft, leftElbow, faceWidth * 0.08, [0.58, 0.5, 0.84], 14),
      cylinderMesh("Proxy_LeftForearm", leftElbow, leftHand, faceWidth * 0.07, [0.58, 0.5, 0.84], 14),
      cylinderMesh("Proxy_RightUpperArm", shoulderRight, rightElbow, faceWidth * 0.08, [0.58, 0.5, 0.84], 14),
      cylinderMesh("Proxy_RightForearm", rightElbow, rightHand, faceWidth * 0.07, [0.58, 0.5, 0.84], 14),
    );
  }

  if (extras.fullBody) {
    const hipLeft: Vec3 = [center[0] - faceWidth * 0.36, pelvisY, center[2] - faceDepth * 0.14];
    const hipRight: Vec3 = [center[0] + faceWidth * 0.36, pelvisY, center[2] - faceDepth * 0.14];
    const kneeLeft: Vec3 = [center[0] - faceWidth * 0.33, pelvisY - faceHeight * 0.78, center[2] - faceDepth * 0.08];
    const kneeRight: Vec3 = [center[0] + faceWidth * 0.33, pelvisY - faceHeight * 0.78, center[2] - faceDepth * 0.08];
    const footLeft: Vec3 = [center[0] - faceWidth * 0.35, pelvisY - faceHeight * 1.48, center[2] + faceDepth * 0.22];
    const footRight: Vec3 = [center[0] + faceWidth * 0.35, pelvisY - faceHeight * 1.48, center[2] + faceDepth * 0.22];
    meshes.push(
      ellipsoidMesh("Proxy_Pelvis", [center[0], pelvisY, center[2] - faceDepth * 0.12], [faceWidth * 0.43, faceHeight * 0.16, faceDepth * 0.58], [0.42, 0.58, 0.82], 16, 8),
      cylinderMesh("Proxy_LeftThigh", hipLeft, kneeLeft, faceWidth * 0.095, [0.5, 0.56, 0.78], 14),
      cylinderMesh("Proxy_LeftShin", kneeLeft, footLeft, faceWidth * 0.082, [0.5, 0.56, 0.78], 14),
      cylinderMesh("Proxy_RightThigh", hipRight, kneeRight, faceWidth * 0.095, [0.5, 0.56, 0.78], 14),
      cylinderMesh("Proxy_RightShin", kneeRight, footRight, faceWidth * 0.082, [0.5, 0.56, 0.78], 14),
    );
  }

  return meshes;
}

function geometryBlock(id: number, name: string, vertices: Vec3[], polygons: number[]): string {
  const flatVertices = vertices.flatMap((vertex) => vertex.map(fmt));
  const polygonIndices: number[] = [];
  const normals: string[] = [];

  for (let index = 0; index + 2 < polygons.length; index += 3) {
    const aIndex = polygons[index];
    const bIndex = polygons[index + 1];
    const cIndex = polygons[index + 2];
    const a = vertices[aIndex];
    const b = vertices[bIndex];
    const c = vertices[cIndex];
    if (!a || !b || !c) continue;

    const normal = normalize(cross(sub(b, a), sub(c, a)));
    polygonIndices.push(aIndex, bIndex, -cIndex - 1);
    normals.push(...normal.map(fmt), ...normal.map(fmt), ...normal.map(fmt));
  }

  const materialIndices = polygonIndices.length ? ["0"] : [];

  return `\tGeometry: ${id}, "Geometry::${name}", "Mesh" {\n` +
    `\t\tVertices: *${flatVertices.length} {\n` +
    `\t\t\ta: ${arr(flatVertices)}\n` +
    `\t\t}\n` +
    `\t\tPolygonVertexIndex: *${polygonIndices.length} {\n` +
    `\t\t\ta: ${arr(polygonIndices)}\n` +
    `\t\t}\n` +
    `\t\tGeometryVersion: 124\n` +
    `\t\tLayerElementNormal: 0 {\n` +
    `\t\t\tVersion: 101\n` +
    `\t\t\tName: ""\n` +
    `\t\t\tMappingInformationType: "ByPolygonVertex"\n` +
    `\t\t\tReferenceInformationType: "Direct"\n` +
    `\t\t\tNormals: *${normals.length} {\n` +
    `\t\t\t\ta: ${arr(normals)}\n` +
    `\t\t\t}\n` +
    `\t\t}\n` +
    `\t\tLayerElementMaterial: 0 {\n` +
    `\t\t\tVersion: 101\n` +
    `\t\t\tName: ""\n` +
    `\t\t\tMappingInformationType: "AllSame"\n` +
    `\t\t\tReferenceInformationType: "IndexToDirect"\n` +
    `\t\t\tMaterials: *${materialIndices.length} {\n` +
    `\t\t\t\ta: ${arr(materialIndices)}\n` +
    `\t\t\t}\n` +
    `\t\t}\n` +
    `\t\tLayer: 0 {\n` +
    `\t\t\tVersion: 100\n` +
    `\t\t\tLayerElement:  {\n` +
    `\t\t\t\tType: "LayerElementNormal"\n` +
    `\t\t\t\tTypedIndex: 0\n` +
    `\t\t\t}\n` +
    `\t\t\tLayerElement:  {\n` +
    `\t\t\t\tType: "LayerElementMaterial"\n` +
    `\t\t\t\tTypedIndex: 0\n` +
    `\t\t\t}\n` +
    `\t\t}\n` +
    `\t}\n`;
}

function modelBlock(id: number, name: string, type: "Mesh" | "Null", translation?: Vec3): string {
  const t = translation ?? [0, 0, 0];
  return `\tModel: ${id}, "Model::${name}", "${type}" {\n` +
    `\t\tVersion: 232\n` +
    `\t\tProperties70:  {\n` +
    `\t\t\tP: "RotationActive", "bool", "", "",1\n` +
    `\t\t\tP: "InheritType", "enum", "", "",1\n` +
    `\t\t\tP: "DefaultAttributeIndex", "int", "Integer", "",0\n` +
    `\t\t\tP: "Lcl Translation", "Lcl Translation", "", "A",${fmt(t[0])},${fmt(t[1])},${fmt(t[2])}\n` +
    `\t\t\tP: "Lcl Rotation", "Lcl Rotation", "", "A",0,0,0\n` +
    `\t\t\tP: "Lcl Scaling", "Lcl Scaling", "", "A",1,1,1\n` +
    `\t\t}\n` +
    `\t\tShading: T\n` +
    `\t\tCulling: "CullingOff"\n` +
    `\t}\n`;
}

function materialBlock(id: number, name: string, color: Vec3): string {
  return `\tMaterial: ${id}, "Material::${name}", "" {\n` +
    `\t\tVersion: 102\n` +
    `\t\tShadingModel: "phong"\n` +
    `\t\tMultiLayer: 0\n` +
    `\t\tProperties70:  {\n` +
    `\t\t\tP: "DiffuseColor", "Color", "", "A",${fmt(color[0])},${fmt(color[1])},${fmt(color[2])}\n` +
    `\t\t\tP: "DiffuseFactor", "Number", "", "A",0.85\n` +
    `\t\t\tP: "SpecularColor", "Color", "", "A",0.08,0.08,0.08\n` +
    `\t\t\tP: "SpecularFactor", "Number", "", "A",0.18\n` +
    `\t\t\tP: "Shininess", "Number", "", "A",20\n` +
    `\t\t}\n` +
    `\t}\n`;
}

function animationCurveBlock(id: number, values: number[], frames: CaptureFrame[]): string {
  const times = frames.map((frame) => Math.round((frame.timeMs / 1000) * FBX_TIME_SECOND));
  return `\tAnimationCurve: ${id}, "AnimCurve::", "" {\n` +
    `\t\tDefault: 0\n` +
    `\t\tKeyVer: 4008\n` +
    `\t\tKeyTime: *${times.length} {\n` +
    `\t\t\ta: ${arr(times)}\n` +
    `\t\t}\n` +
    `\t\tKeyValueFloat: *${values.length} {\n` +
    `\t\t\ta: ${arr(values.map(fmt))}\n` +
    `\t\t}\n` +
    `\t\tKeyAttrFlags: *1 {\n` +
    `\t\t\ta: 24836\n` +
    `\t\t}\n` +
    `\t\tKeyAttrDataFloat: *4 {\n` +
    `\t\t\ta: 0,0,9.419963e-30,0\n` +
    `\t\t}\n` +
    `\t\tKeyAttrRefCount: *1 {\n` +
    `\t\t\ta: ${values.length}\n` +
    `\t\t}\n` +
    `\t}\n`;
}

function animationCurveNodeBlock(id: number, name: string): string {
  return `\tAnimationCurveNode: ${id}, "AnimCurveNode::${name}", "" {\n` +
    `\t\tProperties70:  {\n` +
    `\t\t\tP: "d|X", "Number", "", "A",0\n` +
    `\t\t\tP: "d|Y", "Number", "", "A",0\n` +
    `\t\t\tP: "d|Z", "Number", "", "A",0\n` +
    `\t\t}\n` +
    `\t}\n`;
}

function objectConnection(child: number, parent: number): string {
  return `\tC: "OO",${child},${parent}\n`;
}

function propertyConnection(child: number, parent: number, property: string): string {
  return `\tC: "OP",${child},${parent},"${property}"\n`;
}

function definitionsBlock(counts: Record<FbxType, number>): string {
  const total = Object.values(counts).reduce((sum, count) => sum + count, 1);
  return `Definitions:  {\n` +
    `\tVersion: 100\n` +
    `\tCount: ${total}\n` +
    `\tObjectType: "GlobalSettings" {\n` +
    `\t\tCount: 1\n` +
    `\t}\n` +
    `\tObjectType: "Model" {\n` +
    `\t\tCount: ${counts.Model}\n` +
    `\t\tPropertyTemplate: "FbxNode" {\n` +
    `\t\t\tProperties70:  {\n` +
    `\t\t\t\tP: "Lcl Translation", "Lcl Translation", "", "A",0,0,0\n` +
    `\t\t\t\tP: "Lcl Rotation", "Lcl Rotation", "", "A",0,0,0\n` +
    `\t\t\t\tP: "Lcl Scaling", "Lcl Scaling", "", "A",1,1,1\n` +
    `\t\t\t}\n` +
    `\t\t}\n` +
    `\t}\n` +
    `\tObjectType: "Geometry" {\n` +
    `\t\tCount: ${counts.Geometry}\n` +
    `\t}\n` +
    `\tObjectType: "Material" {\n` +
    `\t\tCount: ${counts.Material}\n` +
    `\t}\n` +
    `\tObjectType: "AnimationStack" {\n` +
    `\t\tCount: ${counts.AnimationStack}\n` +
    `\t}\n` +
    `\tObjectType: "AnimationLayer" {\n` +
    `\t\tCount: ${counts.AnimationLayer}\n` +
    `\t}\n` +
    `\tObjectType: "AnimationCurveNode" {\n` +
    `\t\tCount: ${counts.AnimationCurveNode}\n` +
    `\t}\n` +
    `\tObjectType: "AnimationCurve" {\n` +
    `\t\tCount: ${counts.AnimationCurve}\n` +
    `\t}\n` +
    `}\n`;
}

function documentsBlock(documentId: number): string {
  return `Documents:  {\n` +
    `\tCount: 1\n` +
    `\tDocument: ${documentId}, "Scene", "Scene" {\n` +
    `\t\tProperties70:  {\n` +
    `\t\t\tP: "SourceObject", "object", "", ""\n` +
    `\t\t}\n` +
    `\t\tRootNode: 0\n` +
    `\t}\n` +
    `}\n\n` +
    `References:  {\n` +
    `}\n`;
}

function globalSettingsBlock(): string {
  return `GlobalSettings:  {\n` +
    `\tVersion: 1000\n` +
    `\tProperties70:  {\n` +
    `\t\tP: "UpAxis", "int", "Integer", "",1\n` +
    `\t\tP: "UpAxisSign", "int", "Integer", "",1\n` +
    `\t\tP: "FrontAxis", "int", "Integer", "",2\n` +
    `\t\tP: "FrontAxisSign", "int", "Integer", "",1\n` +
    `\t\tP: "CoordAxis", "int", "Integer", "",0\n` +
    `\t\tP: "CoordAxisSign", "int", "Integer", "",1\n` +
    `\t\tP: "OriginalUpAxis", "int", "Integer", "",1\n` +
    `\t\tP: "OriginalUpAxisSign", "int", "Integer", "",1\n` +
    `\t\tP: "UnitScaleFactor", "double", "Number", "",1\n` +
    `\t\tP: "OriginalUnitScaleFactor", "double", "Number", "",1\n` +
    `\t}\n` +
    `}\n`;
}

function headerBlock(session: CaptureSession): string {
  const created = new Date(session.createdAt);
  return `; FBX 7.4.0 project file\n` +
    `; Created by ${APP_NAME}\n\n` +
    `FBXHeaderExtension:  {\n` +
    `\tFBXHeaderVersion: 1003\n` +
    `\tFBXVersion: 7400\n` +
    `\tEncryptionType: 0\n` +
    `\tCreationTimeStamp:  {\n` +
    `\t\tVersion: 1000\n` +
    `\t\tYear: ${created.getUTCFullYear()}\n` +
    `\t\tMonth: ${created.getUTCMonth() + 1}\n` +
    `\t\tDay: ${created.getUTCDate()}\n` +
    `\t\tHour: ${created.getUTCHours()}\n` +
    `\t\tMinute: ${created.getUTCMinutes()}\n` +
    `\t\tSecond: ${created.getUTCSeconds()}\n` +
    `\t\tMillisecond: ${created.getUTCMilliseconds()}\n` +
    `\t}\n` +
    `\tCreator: "${APP_NAME}"\n` +
    `}\n`;
}

function buildScene(session: CaptureSession, triangles: number[], options: BuildOptions): string {
  const first = session.frames[0];
  const firstPoseFrame = session.frames.find((frame) => frame.poseLandmarks?.length);
  const nextId = idFactory();
  const documentId = nextId();
  const rootNullId = nextId();
  const faceGeometryId = nextId();
  const faceModelId = nextId();
  const markerGeometryId = nextId();
  const markerModelId = nextId();
  const headModelId = nextId();
  const blendshapeRootId = nextId();
  const meshMaterialId = nextId();
  const markerMaterialId = nextId();
  const state = createBuildState();
  const poseModelRefs: Array<{ modelId: number; name: string; index: number; fallback: Vec3 }> = [];

  const faceVertices = first.landmarks.map(landmarkToWorld);
  const facePolygons = validTriangles(triangles, faceVertices.length);
  const marker = markerGeometry(first.landmarks);
  const markerPolygons = marker.polygons.flat();

  addObject(state, "Model", modelBlock(rootNullId, "FaceCapture_Root", "Null"));
  addObject(state, "Geometry", geometryBlock(faceGeometryId, "FaceMesh", faceVertices, facePolygons.flat()));
  addObject(state, "Model", modelBlock(faceModelId, "FaceMesh_Static", "Mesh"));
  addObject(state, "Geometry", geometryBlock(markerGeometryId, "FaceLandmarkMarkers", marker.vertices, markerPolygons.length ? markerPolygons : fallbackPolygons(marker.vertices.length).flat()));
  addObject(state, "Model", modelBlock(markerModelId, "FaceLandmark_Markers", "Mesh"));
  addObject(state, "Model", modelBlock(headModelId, "Head_Transform", "Null", matrixTranslation(first)));
  addObject(state, "Model", modelBlock(blendshapeRootId, "Blendshape_Scores", "Null"));
  addObject(state, "Material", materialBlock(meshMaterialId, "Tony Marghella_FaceMesh", [0.36, 0.92, 0.74]));
  addObject(state, "Material", materialBlock(markerMaterialId, "Tony Marghella_Landmarks", [0.96, 0.78, 0.26]));

  state.connections.push(objectConnection(rootNullId, 0));
  state.connections.push(objectConnection(faceModelId, rootNullId));
  state.connections.push(objectConnection(markerModelId, rootNullId));
  state.connections.push(objectConnection(headModelId, rootNullId));
  state.connections.push(objectConnection(blendshapeRootId, rootNullId));
  state.connections.push(objectConnection(faceGeometryId, faceModelId));
  state.connections.push(objectConnection(markerGeometryId, markerModelId));
  state.connections.push(objectConnection(meshMaterialId, faceModelId));
  state.connections.push(objectConnection(markerMaterialId, markerModelId));

  if (firstPoseFrame?.poseLandmarks?.length) {
    const poseRootId = nextId();
    addObject(state, "Model", modelBlock(poseRootId, "BodyPose_Landmarks", "Null"));
    state.connections.push(objectConnection(poseRootId, rootNullId));

    firstPoseFrame.poseLandmarks.forEach((landmark, index) => {
      const modelId = nextId();
      const name = `POSE_${String(index).padStart(2, "0")}`;
      const fallback = poseLandmarkToWorld(landmark);
      addObject(state, "Model", modelBlock(modelId, name, "Null", fallback));
      state.connections.push(objectConnection(modelId, poseRootId));
      poseModelRefs.push({ modelId, name, index, fallback });
    });
  }

  const proxyFrame = first.poseLandmarks?.length ? first : firstPoseFrame ?? first;
  for (const proxy of proxyGeometry(proxyFrame, options.extras)) {
    const geometryId = nextId();
    const modelId = nextId();
    const materialId = nextId();
    addObject(state, "Geometry", geometryBlock(geometryId, proxy.name, proxy.vertices, proxy.polygons.flat()));
    addObject(state, "Model", modelBlock(modelId, proxy.name, "Mesh"));
    addObject(state, "Material", materialBlock(materialId, proxy.name, proxy.color));
    state.connections.push(objectConnection(modelId, rootNullId));
    state.connections.push(objectConnection(geometryId, modelId));
    state.connections.push(objectConnection(materialId, modelId));
  }

  if (options.includeAnimation) {
    const stackId = nextId();
    const layerId = nextId();
    addObject(state, "AnimationStack", `\tAnimationStack: ${stackId}, "AnimStack::FaceCapture", "" {\n\t}\n`);
    addObject(state, "AnimationLayer", `\tAnimationLayer: ${layerId}, "AnimLayer::BaseLayer", "" {\n\t}\n`);
    state.connections.push(objectConnection(stackId, 0));
    state.connections.push(objectConnection(layerId, stackId));

    const addTranslationAnimation = (modelId: number, name: string, values: Vec3[]) => {
      const nodeId = nextId();
      const xId = nextId();
      const yId = nextId();
      const zId = nextId();
      addObject(state, "AnimationCurveNode", animationCurveNodeBlock(nodeId, `${name}_T`));
      addObject(state, "AnimationCurve", animationCurveBlock(xId, values.map((item) => item[0]), session.frames));
      addObject(state, "AnimationCurve", animationCurveBlock(yId, values.map((item) => item[1]), session.frames));
      addObject(state, "AnimationCurve", animationCurveBlock(zId, values.map((item) => item[2]), session.frames));
      state.connections.push(objectConnection(nodeId, layerId));
      state.connections.push(propertyConnection(nodeId, modelId, "Lcl Translation"));
      state.connections.push(propertyConnection(xId, nodeId, "d|X"));
      state.connections.push(propertyConnection(yId, nodeId, "d|Y"));
      state.connections.push(propertyConnection(zId, nodeId, "d|Z"));
    };

    addTranslationAnimation(headModelId, "Head_Transform", session.frames.map(matrixTranslation));

    first.landmarks.forEach((landmark, index) => {
      const modelId = nextId();
      const name = `LM_${String(index).padStart(3, "0")}`;
      addObject(state, "Model", modelBlock(modelId, name, "Null", landmarkToWorld(landmark)));
      state.connections.push(objectConnection(modelId, rootNullId));
      addTranslationAnimation(
        modelId,
        name,
        session.frames.map((frame) => landmarkToWorld(frame.landmarks[index] ?? landmark)),
      );
    });

    for (const poseRef of poseModelRefs) {
      addTranslationAnimation(
        poseRef.modelId,
        poseRef.name,
        poseAnimationValues(session.frames, poseRef.index, poseRef.fallback),
      );
    }

    const blendshapeNames = Array.from(
      new Set(session.frames.flatMap((frame) => frame.blendshapes.map((item) => item.name))),
    ).sort();

    for (const rawName of blendshapeNames) {
      const safeName = `BS_${cleanName(rawName)}`;
      const modelId = nextId();
      addObject(state, "Model", modelBlock(modelId, safeName, "Null"));
      state.connections.push(objectConnection(modelId, blendshapeRootId));
      addTranslationAnimation(
        modelId,
        safeName,
        session.frames.map((frame) => {
          const score = frame.blendshapes.find((item) => item.name === rawName)?.score ?? 0;
          return [score * 100, 0, 0];
        }),
      );
    }
  }

  return `${headerBlock(session)}\n` +
    `${globalSettingsBlock()}\n` +
    `${documentsBlock(documentId)}\n` +
    `${definitionsBlock(state.counts)}\n\n` +
    `Objects:  {\n${state.objects.join("")}}\n\n` +
    `Connections:  {\n${state.connections.join("")}}\n\n` +
    `Takes:  {\n` +
    `\tCurrent: ""\n` +
    `}\n`;
}

export function buildFbx(session: CaptureSession, triangles: number[], options: BuildOptions = {}): string {
  return buildScene(session, triangles, { includeAnimation: true, ...options });
}

export function buildStaticFbx(session: CaptureSession, triangles: number[], extras?: ExportExtras): string {
  return buildScene(session, triangles, { includeAnimation: false, extras });
}
